/* MissPet — light motion.
 *
 * Fail-open by construction: this script adds BOTH the class that hides an
 * element and the observer that reveals it. If the file 404s, throws, or is
 * blocked, nothing is ever hidden and the page renders exactly as it does
 * today. Never mark reveal targets in Liquid — that makes content depend on
 * JavaScript succeeding in order to be visible.
 *
 * Motion is also skipped entirely when the visitor asks for reduced motion.
 */
(() => {
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  if (reduced.matches) return;

  // Sections worth revealing. The hero is excluded: it is above the fold on
  // every load, so fading it in only delays the first thing a visitor sees.
  const targets = [
    ...document.querySelectorAll('#MainContent > [id^="shopify-section"]'),
  ].slice(1);

  if (!targets.length || !('IntersectionObserver' in window)) return;

  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        entry.target.classList.add('mp-in');
        observer.unobserve(entry.target);
      }
    },
    // A little before the element arrives, so the movement finishes as it
    // reaches comfortable reading position rather than starting there.
    { rootMargin: '0px 0px -8% 0px', threshold: 0.02 }
  );

  for (const el of targets) {
    el.classList.add('mp-reveal');
    observer.observe(el);
  }

  // Safety net. If anything stops the observer from firing — a frozen tab, a
  // browser quirk, a section that never intersects because it sits inside a
  // container that does not scroll — reveal everything after a moment rather
  // than leaving content invisible.
  setTimeout(() => {
    for (const el of targets) el.classList.add('mp-in');
  }, 2500);
})();

/* Sticky add-to-cart <-> floating buttons.
 * The bar sets data-stuck="true" on .sticky-add-to-cart__bar while shown.
 * Mirror that onto <html> so CSS can lift the body-level WhatsApp button clear
 * of the bar (CSS can't reach a body-level float from the bar's own attribute).
 * Separate IIFE so it still runs under prefers-reduced-motion. Fail-safe: if
 * the bar isn't present (non-product pages) it simply does nothing. */
(() => {
  const init = () => {
    const bar = document.querySelector('.sticky-add-to-cart__bar');
    if (!bar) return;
    const sync = () =>
      document.documentElement.classList.toggle('mp-atc-stuck', bar.dataset.stuck === 'true');
    new MutationObserver(sync).observe(bar, { attributes: true, attributeFilter: ['data-stuck'] });
    sync();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();

/* MissPet — geo language routing.
 *
 * Goal: a visitor located in Israel stays on the Hebrew storefront; a visitor
 * anywhere else is sent to the English one (/en/...). The language picker is
 * untouched, and a manual choice always wins over this script.
 *
 * Country comes from Shopify's own detection endpoint, which reports the real
 * IP country even on plans without automatic market selection. Everything is
 * fail-open: any error, and the visitor simply stays where they are.
 */
(() => {
  const KEY_CHOICE = 'mp-locale-choice';   // persists a manual pick
  const KEY_MOVED = 'mp-geo-moved';        // one auto-move per session

  // Remember an explicit pick from the language selector so we never override
  // it on later visits. Registered first, and independently of the redirect
  // below, so it keeps working even once the visitor is already on /en/.
  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (form instanceof HTMLFormElement && /\/localization/.test(form.getAttribute('action') || '')) {
      try { localStorage.setItem(KEY_CHOICE, '1'); } catch (e) {}
    }
  }, true);

  // Only the Hebrew storefront redirects. On /en/ there is nothing to do.
  if (window.Shopify?.locale !== 'he') return;
  if (window.Shopify?.designMode) return;             // never inside the editor
  if (location.pathname.startsWith('/en/') || location.pathname === '/en') return;

  // Search engines must see the Hebrew page they requested, not a redirect.
  if (/bot|crawl|spider|slurp|bing|google|facebook|embed|preview|lighthouse/i.test(navigator.userAgent)) return;

  try {
    if (localStorage.getItem(KEY_CHOICE)) return;
    if (sessionStorage.getItem(KEY_MOVED)) return;
  } catch (e) { return; }

  const root = window.Shopify?.routes?.root || '/';
  fetch(`${root}browsing_context_suggestions.json?country[enabled]=true`, { headers: { Accept: 'application/json' } })
    .then((response) => (response.ok ? response.json() : null))
    .then((data) => {
      const country = data?.detected_values?.country?.handle;
      // No reading, or a reader actually in Israel: leave the page alone.
      if (!country || country === 'IL') return;
      try { sessionStorage.setItem(KEY_MOVED, '1'); } catch (e) {}
      location.replace(`/en${location.pathname}${location.search}${location.hash}`);
    })
    .catch(() => {});
})();
